import React from "react";
import {
  UserCircle,
  Users,
  Settings,
  Menu,
  Sun,
  Moon,
  ChevronDown,
  LogOut,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { logout } from "../../services/oprations/authAPI";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "../ui/drop-down";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/Avatar";

const Navbar = ({ isCollapsed, setCollapsed, darkMode, toggleTheme }) => {
  const { user } = useSelector((state) => state.profile);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const settingsLinks = [
    {
      to: "/dashboard/settings/profile-management",
      label: "Profile Management",
      icon: UserCircle,
    },
    {
      to: "/dashboard/settings/password-management",
      label: "Password & Authentication",
      icon: Settings,
    },
    ...(user?.role === "Admin"
      ? [
          {
            to: "/dashboard/settings/user-management",
            label: "User Management",
            icon: Users,
          },
        ]
      : []),
  ];

  const handleLogout = () => {
    dispatch(logout(navigate));
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const names = name.split(" ");
    if (names.length > 1) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <header
      className={`p-4 border-b flex justify-between items-center flex-shrink-0 transition-colors duration-300 ${
        darkMode ? "bg-[#00254D] text-white" : "bg-white text-black"
      }`}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={() => setCollapsed(!isCollapsed)}
          className={`p-2 rounded-md transition-colors ${
            darkMode ? "hover:bg-[#003366]" : "hover:bg-gray-100"
          }`}
        >
          <Menu />
        </button>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={toggleTheme}
          className={`p-2 rounded-full transition-colors ${
            darkMode ? "hover:bg-[#003366]" : "hover:bg-gray-100"
          }`}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>


        {/* User Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`flex items-center space-x-2 cursor-pointer p-1 rounded-full transition-colors ${
                darkMode
                  ? "hover:bg-[#003366] text-white"
                  : "hover:bg-gray-100 text-black"
              }`}
            >
              <Avatar className="h-9 w-9">
                {user?.image ? (
                  <AvatarImage src={user.image} alt={user.name} />
                ) : (
                  <AvatarFallback className="bg-[#012950] text-white">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                )}
              </Avatar>

              <span className="hidden sm:inline font-semibold text-sm">
                {user?.name || "User"}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-64" align="end">
            <DropdownMenuLabel>
              <p className="text-sm font-bold text-gray-900 truncate">
                {user?.name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              <p className="text-xs text-gray-500 truncate">{user?.role}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {settingsLinks.map((link) => (
              <Link to={link.to} key={link.to}>
                <DropdownMenuItem className="cursor-pointer">
                  <link.icon className="mr-2 h-4 w-4" />
                  <span>{link.label}</span>
                </DropdownMenuItem>
              </Link>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-red-600 cursor-pointer focus:bg-red-50 focus:text-red-600"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Navbar;
